import { websocketService, RecordingEventMessage, RecordingEventCallback } from "./websocketService";

// Recording status mapping aligned with RecordingPreparationScreen
const STATUS_MAP = {
  process_initiated: "processing",
  download_started: "processing",
  upload_started: "uploading",
  upload_completed: "uploaded", 
  transcribe_started: "transcribing",
  transcribe_completed: "transcribed",
  scribe_started: "ai processing",
  scribe_completed: "completed",
  completed: "completed",
  processing_started: "processing",
  processing_completed: "completed",
  analysis_started: "analyzing",
  analysis_completed: "analyzed",
  failed: "failed",
  error: "failed"
} as const;

// Completion statuses
const COMPLETED_STATUSES = ["completed", "analyzed"];

// UUID to number conversion helper (consistent hash)
function convertUuidToNumber(uuid: string): number {
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    const char = uuid.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// Map event type to status
function mapEventTypeToStatus(eventType: string): string {
  return STATUS_MAP[eventType as keyof typeof STATUS_MAP] || eventType;
}

// Check if status indicates completion
function isCompletedStatus(status: string): boolean {
  return COMPLETED_STATUSES.includes(status);
}

interface RecordingUpdateData {
  id: number;
  recording_id: number;
  status: string;
  progress: number;
  event_type: string;
  service: string;
  user_id: number;
  timestamp: string;
  metadata: any;
  original_uuid: string;
}

type RecordingUpdateCallback = (data: RecordingUpdateData) => void;

class SSEService {
  private activeListeners: Set<() => void> = new Set();

  /**
   * Initialize WebSocket connection
   */
  async initializeFirebase(): Promise<string | null> {
    console.log("🔌 Initializing WebSocket connection...");
    
    try {
      const connected = await websocketService.connect();
      
      if (connected) {
        console.log("✅ WebSocket connection established successfully");
        return "websocket_user";
      } else {
        console.log("❌ WebSocket connection failed");
        return null;
      }
    } catch (error) {
      console.error("❌ WebSocket initialization error:", error);
      return null;
    }
  }

  /**
   * Set up listener for all user recordings
   */
  async listenToUserRecordings(
    onStatusUpdate?: (
      recordingId: string,
      status: string,
      progress: number,
      details?: any
    ) => void
  ) {
    console.log("📡 Setting up WebSocket listener for all recordings");
    
    try {
      // Ensure connection
      const connected = await websocketService.connect();
      if (!connected) {
        console.log("❌ WebSocket connection failed - falling back to API polling");
        return {
          success: false,
          disconnect: () => console.log("No WebSocket connection to disconnect"),
        };
      }

      // Create recording event handler
      const handleRecordingEvent: RecordingEventCallback = (event: RecordingEventMessage) => {
        if (!onStatusUpdate) return;

        const recordingId = event.recording_id;
        const status = mapEventTypeToStatus(event.event_type);
        
        console.log(`📡 SSE Processing: ${event.event_type} -> ${status} (${event.progress}%) for recording ${recordingId}`);
        console.log(`📡 Raw WebSocket event:`, event);
        
        // Call the status update callback
        onStatusUpdate(
          recordingId,
          status,
          event.progress,
          {
            event_type: event.event_type,
            service: event.service,
            timestamp: event.timestamp,
            metadata: event.metadata,
            original_recording_id: event.recording_id,
            is_processing: !isCompletedStatus(status),
            is_uploaded: event.progress > 0,
            is_processed: isCompletedStatus(status),
            has_error: status.toLowerCase().includes('failed') || status.toLowerCase().includes('error'),
            progress: event.progress,
            message: `${event.event_type}: ${event.progress}%`,
          }
        );
      };
      
      // Add listener to WebSocket service
      const unsubscribe = websocketService.addListener(handleRecordingEvent);
      this.activeListeners.add(unsubscribe);
      
      console.log("✅ WebSocket listener setup successful");
      
      return {
        success: true,
        disconnect: () => {
          console.log("🧹 Disconnecting WebSocket listener");
          unsubscribe();
          this.activeListeners.delete(unsubscribe);
        },
      };

    } catch (error) {
      console.error("❌ Error setting up WebSocket listener:", error);
      return {
        success: false,
        disconnect: () => console.log("No WebSocket connection to disconnect"),
      };
    }
  }

  /**
   * Listen to a specific recording
   */
  async listenToRecording(
    recordingId: number,
    onStatusUpdate?: (
      recordingId: number,
      status: string,
      progress: number,
      details?: any
    ) => void
  ) {
    console.log(`📡 Setting up WebSocket listener for recording ${recordingId}`);
    
    try {
      // Ensure connection
      const connected = await websocketService.connect();
      if (!connected) {
        console.log("❌ WebSocket connection failed");
        return () => console.log("No WebSocket listener to disconnect");
      }

      // Create recording event handler for specific recording
      const handleRecordingEvent: RecordingEventCallback = (event: RecordingEventMessage) => {
        if (!onStatusUpdate) return;

        const eventRecordingId = convertUuidToNumber(event.recording_id);
        
        // Only process events for the specific recording we're listening to
        if (eventRecordingId === recordingId) {
          const status = mapEventTypeToStatus(event.event_type);
          
          console.log(`📡 Recording ${recordingId} event: ${event.event_type} -> ${status} (${event.progress}%)`);
          
          onStatusUpdate(
            recordingId,
            status,
            event.progress,
            {
              event_type: event.event_type,
              service: event.service,
              timestamp: event.timestamp,
              metadata: event.metadata,
              original_recording_id: event.recording_id,
            }
          );
        }
      };
      
      // Add listener to WebSocket service
      const unsubscribe = websocketService.addListener(handleRecordingEvent);
      this.activeListeners.add(unsubscribe);
      
      // Return disconnect function
      return () => {
        console.log(`🧹 Unsubscribing from recording ${recordingId}`);
        unsubscribe();
        this.activeListeners.delete(unsubscribe);
      };

    } catch (error) {
      console.error(`❌ Error setting up WebSocket listener for recording ${recordingId}:`, error);
      return () => console.log("No WebSocket listener to disconnect");
    }
  }

  /**
   * Set up WebSocket listener for recording updates (new clean interface)
   */
  setupRecordingListener(callback: RecordingUpdateCallback): () => void {
    console.log("📡 Setting up WebSocket listener for recording updates");
    
    // Create recording event handler
    const handleRecordingEvent: RecordingEventCallback = (event: RecordingEventMessage) => {
      console.log(`📡 Processing recording event: ${event.event_type} -> ${mapEventTypeToStatus(event.event_type)} (${event.progress}%)`);
      
      // Convert UUID to number for compatibility with existing code
      const recordingId = convertUuidToNumber(event.recording_id);
      
      // Map the event to the expected format
      const mappedData: RecordingUpdateData = {
        id: recordingId,
        recording_id: recordingId,
        status: mapEventTypeToStatus(event.event_type),
        progress: event.progress,
        event_type: event.event_type,
        service: event.service,
        user_id: event.user_id,
        timestamp: event.timestamp,
        metadata: event.metadata,
        // Keep original UUID for reference
        original_uuid: event.recording_id
      };
      
      callback(mappedData);
    };
    
    // Add listener to WebSocket service
    const unsubscribe = websocketService.addListener(handleRecordingEvent);
    
    // Track active listeners for cleanup
    this.activeListeners.add(unsubscribe);
    
    console.log("✅ WebSocket listener setup successful");
    
    // Return cleanup function
    return () => {
      console.log("🧹 Cleaning up WebSocket listener");
      unsubscribe();
      this.activeListeners.delete(unsubscribe);
    };
  }

  /**
   * Close all WebSocket listeners
   */
  closeAllListeners(): void {
    console.log("🔌 Closing all WebSocket listeners");
    
    // Clean up all active listeners
    this.activeListeners.forEach(unsubscribe => unsubscribe());
    this.activeListeners.clear();
  }

  /**
   * Disconnect all WebSocket listeners
   */
  disconnect(): void {
    console.log("🔌 Disconnecting all WebSocket listeners");
    
    // Clean up all active listeners
    this.activeListeners.forEach(unsubscribe => unsubscribe());
    this.activeListeners.clear();
  }

  /**
   * Get WebSocket connection status
   */
  getConnectionStatus(): string {
    return websocketService.getState();
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return websocketService.isConnected();
  }

  /**
   * Convert UUID string to a number for compatibility with existing code
   */
  convertUuidToNumber(uuid: string): number {
    return convertUuidToNumber(uuid);
  }

  /**
   * Map event_type to user-friendly status
   */
  mapEventTypeToStatus(eventType: string, status?: string | null): string {
    // Use the status field if available, otherwise map event_type
    if (status) {
      return status;
    }
    return mapEventTypeToStatus(eventType);
  }

  /**
   * Check if a status indicates completion
   */
  isCompletedStatus(status: string): boolean {
    return isCompletedStatus(status);
  }
}

// Export singleton instance
export const sseService = new SSEService();
